/**
 * QR gun tutorial — who is offered it, how a scan is read inside it and how it is resumed.
 *
 * Why this exists: Deleito closes orders by scanning the ticket's QR with a Bluetooth gun (src/qrScanner.js).
 * The kitchen has to pair it with the tablet, use it and wake it when it falls asleep, without technical
 * training; QrGunTutorialScreen (docs/tutorial-pistola-qr.md) teaches it, from Ajustes only. This module
 * holds the decisions around it, pure and with the storage injected so they are unit tested without a
 * tablet: which clients see it, what a scan means while it is open, and how it is picked up again when
 * Android kills the app while the person is in the tablet's Bluetooth settings.
 *
 * Every storage and telemetry call here is best-effort: a tablet whose storage throws must still work.
 */
import posthog from 'posthog-js';

/**
 * The seven numbered steps, in order; the screen shows them as "Paso 1..7 de 7". The connection comes first
 * and is proved by the test (step 4); then how it works (the bag, the toast) and what to do when it sleeps.
 */
export const QR_GUN_TUTORIAL_STEPS = Object.freeze(['power', 'pair', 'back', 'test', 'scan', 'notify', 'sleep']);

/** Off the numbered path: the branch of `pair` when the gun does not show up, and the end. */
export const QR_GUN_TUTORIAL_NOTFOUND = 'notfound';
export const QR_GUN_TUTORIAL_DONE = 'done';

/** Clients that see the tutorial in Ajustes: Deleito's three, which use the gun, and Bronze's own (to demo and
 *  support it; Adrián, 22-oct-2026). An explicit list, not a prefix. */
export const QR_GUN_TUTORIAL_CLIENTS = Object.freeze(['DELEITO', 'DELEITO_MADRID', 'DELEITO_RESTAURACIO', 'BRONZE']);

export function isQrGunTutorialClient(client) {
  return typeof client === 'string' && QR_GUN_TUTORIAL_CLIENTS.includes(client);
}

/** How the screen was left. */
export const QR_GUN_OUTCOME = Object.freeze({ COMPLETED: 'completed', DISMISSED: 'dismissed' });

/**
 * The order id of the test QR (src/assets/qr-gun-tutorial/test-qr.svg, made by tools/qr-gun-render): it
 * rides the same URL as a ticket, so it takes the same road through qrScanner.js, and no order has this id.
 */
export const QR_GUN_TEST_ORDER_ID = 'tracker-tutorial-test';

/**
 * What a gun read means to the tutorial. `scan` is qrScanner.js `readScan()`'s result.
 *   test_code: the tutorial's own QR · ticket: a real ticket (it counts too, and closes nothing) ·
 *   partial: a feedback QR that arrived cut · other: anything else.
 */
export function classifyTutorialScan(scan) {
  if (!scan) return 'other';
  if (scan.orderId === QR_GUN_TEST_ORDER_ID) return 'test_code';
  if (scan.orderId) return 'ticket';
  if (scan.partial) return 'partial';
  return 'other';
}

// ---- resuming after Android kills the app in the Bluetooth settings ----

export const QR_GUN_RESUME_KEY = 'bronze:qrGunTutorial:resume';
/** A trip to Bluetooth settings takes minutes; an older mark is a tutorial abandoned, not paused. */
export const QR_GUN_RESUME_TTL_MS = 15 * 60 * 1000;
/** The steps where the person may leave the app: looking for the gun, and coming back to Bronze. */
export const QR_GUN_RESUME_STEPS = Object.freeze(['pair', QR_GUN_TUTORIAL_NOTFOUND, 'back']);

/** Mark that the tutorial is paused at `now`. Returns whether it was written; never throws. */
export function writeQrGunResume(storage, now = Date.now()) {
  if (!storage) return false;
  try {
    storage.setItem(QR_GUN_RESUME_KEY, JSON.stringify({ at: now }));
    return true;
  } catch (_) {
    return false;
  }
}

export function clearQrGunResume(storage) {
  if (!storage) return false;
  try {
    storage.removeItem(QR_GUN_RESUME_KEY);
    return true;
  } catch (_) {
    return false;
  }
}

/** Whether a fresh mark is there: written less than QR_GUN_RESUME_TTL_MS before `now`. */
export function readQrGunResume(storage, now = Date.now()) {
  if (!storage) return false;
  try {
    const raw = storage.getItem(QR_GUN_RESUME_KEY);
    if (!raw) return false;
    const at = Number(JSON.parse(raw)?.at);
    if (!Number.isFinite(at)) return false;
    const age = now - at;
    return age >= 0 && age <= QR_GUN_RESUME_TTL_MS;
  } catch (_) {
    return false;
  }
}

/** Keep the mark in step with the screen: written on the steps that leave the app, cleared everywhere else. */
export function syncQrGunResume(storage, step, now = Date.now()) {
  return QR_GUN_RESUME_STEPS.includes(step) ? writeQrGunResume(storage, now) : clearQrGunResume(storage);
}

/** Product event `qr_gun_tutorial_<event>`. Silent in the published demo; never throws. */
export function trackQrGun(event, props = {}) {
  try {
    // typeof: the constant only exists in the webpack build (see vue.config.js), not in vitest.
    if (typeof __BRONZE_DEMO__ !== 'undefined' && __BRONZE_DEMO__) return;
    posthog.capture(`qr_gun_tutorial_${event}`, props);
  } catch (_) { /* never let telemetry throw */ }
}
